// StatsCards.jsx
import { motion } from 'framer-motion';
import {
  Ticket,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const StatsCards = ({ data }) => {
  const defaultStats = {
    totalTickets: 0,
    openTickets: 0,
    closedTickets: 0,
    criticalTickets: 0,
    trends: {
      totalTickets: 0,
      openTickets: 0,
      closedTickets: 0,
      criticalTickets: 0
    }
  };

  const stats = data || defaultStats;

  const cards = [
    {
      title: 'Total Tickets',
      value: stats.totalTickets,
      icon: Ticket,
      color: 'blue',
      trend: stats.trends?.totalTickets || 0,
      description: 'All time tickets'
    },
    {
      title: 'Open Tickets',
      value: stats.openTickets,
      icon: Clock,
      color: 'orange',
      trend: stats.trends?.openTickets || 0,
      description: 'Currently active'
    },
    {
      title: 'Resolved',
      value: stats.closedTickets,
      icon: CheckCircle,
      color: 'green',
      trend: stats.trends?.closedTickets || 0,
      description: 'Successfully closed'
    },
    {
      title: 'Critical',
      value: stats.criticalTickets,
      icon: AlertTriangle,
      color: 'red',
      trend: stats.trends?.criticalTickets || 0,
      description: 'High priority issues'
    }
  ];

  const colorMap = {
    blue: {
      iconBg: 'var(--blue-500, #3b82f6)',
      iconBgSoft: 'rgba(59,130,246,0.08)',
      text: 'var(--blue-600, #2563eb)',
      border: 'rgba(59,130,246,0.08)'
    },
    orange: {
      iconBg: 'var(--orange-500, #f97316)',
      iconBgSoft: 'rgba(249,115,22,0.08)',
      text: 'var(--orange-600, #ea580c)',
      border: 'rgba(249,115,22,0.08)'
    },
    green: {
      iconBg: 'var(--green-500, #10b981)',
      iconBgSoft: 'rgba(16,185,129,0.08)',
      text: 'var(--green-600, #059669)',
      border: 'rgba(16,185,129,0.08)'
    },
    red: {
      iconBg: 'var(--red-500, #ef4444)',
      iconBgSoft: 'rgba(239,68,68,0.08)',
      text: 'var(--red-600, #dc2626)',
      border: 'rgba(239,68,68,0.08)'
    }
  };

  const formatNumber = (num) => {
    if (typeof num !== 'number') return String(num ?? 0);
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return TrendingUp;
    if (trend < 0) return TrendingDown;
    return null;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'var(--green-600, #059669)';
    if (trend < 0) return 'var(--red-600, #dc2626)';
    return 'var(--gray-400, #9ca3af)';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline styles
  const styles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
      gap: '1rem'
    },
    card: (borderColor) => ({
      background: '#fff',
      borderRadius: '1rem',
      padding: '1rem',
      boxShadow: '0 8px 24px rgba(2,6,23,0.04)',
      border: `1px solid ${borderColor}`,
      transition: 'transform 160ms ease, box-shadow 160ms ease',
      overflow: 'hidden'
    }),
    cardInner: { display: 'flex', gap: '1rem', alignItems: 'flex-start' },
    iconBox: (bg) => ({
      width: 48,
      height: 48,
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: bg,
      flexShrink: 0
    }),
    value: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: 'var(--gray-900, #0f172a)',
      margin: 0
    },
    title: { margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--gray-600, #4b5563)' },
    desc: { margin: '0.25rem 0 0 0', fontSize: '0.8125rem', color: 'var(--gray-500, #6b7280)' },
    trendWrap: { display: 'flex', alignItems: 'center', gap: 6 },
    progressBarTrack: {
      width: '100%',
      height: 8,
      background: 'rgba(15,23,42,0.06)',
      borderRadius: 9999,
      overflow: 'hidden',
      marginTop: 12
    },
    progressBarFill: (color) => ({
      height: '100%',
      borderRadius: 9999,
      background: color,
      width: '0%'
    })
  };

  return (
    <motion.div
      style={styles.grid}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {cards.map((card, index) => {
        const Icon = card.icon;
        const TrendIcon = getTrendIcon(card.trend);
        const colors = colorMap[card.color] || colorMap.blue;
        const borderColor = colors.border;
        const iconBg = colors.iconBg;
        const trendColor = getTrendColor(card.trend);

        // progress percentage for Open Tickets card (using closed vs total)
        const progressPercent =
          card.title === 'Open Tickets' && stats.totalTickets > 0
            ? Math.round((stats.closedTickets / stats.totalTickets) * 100)
            : 0;

        return (
          <motion.div
            key={card.title}
            style={styles.card(borderColor)}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.32, delay: index * 0.06 }}
            whileHover={{ translateY: -6 }}
          >
            <div style={styles.cardInner}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={styles.iconBox(iconBg)} aria-hidden="true">
                      <Icon style={{ width: 20, height: 20, color: 'white' }} />
                    </div>

                    <div>
                      <motion.h3
                        style={styles.value}
                        key={card.value}
                        initial={{ scale: 1.04, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.32 }}
                      >
                        {formatNumber(card.value)}
                      </motion.h3>

                      <p style={styles.title}>{card.title}</p>
                    </div>
                  </div>

                  {TrendIcon && (
                    <div style={styles.trendWrap}>
                      <TrendIcon style={{ width: 16, height: 16, color: trendColor }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: trendColor }}>
                        {Math.abs(card.trend)}%
                      </span>
                    </div>
                  )}
                </div>

                <p style={styles.desc}>{card.description}</p>

                {card.title === 'Open Tickets' && stats.totalTickets > 0 && (
                  <div style={styles.progressBarTrack} aria-hidden>
                    <motion.div
                      style={styles.progressBarFill(colors.iconBg)}
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercent}%` }}
                      transition={{ duration: 0.9, delay: 0.3 }}
                    />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default StatsCards;
